// useInlineToolbarEditContext — wires a Query Editor tab's Monaco buffer to
// the SR-Edit pipeline (`useVqeEditWiring`) for the compact popover and the
// Query Editor's Visual mode. Edits apply through Monaco `executeEdits`, one
// undo stop each on the editor's own stack; the whole-value `setValue` model
// adapter is only the fallback when the editor handle is gone.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Inline Toolbar Activation.

import { useMemo } from 'react';

import type { ParseOptions } from '../../../sql-grammar/model';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { VqeEditContext } from '../edit/editContext';
import { useVqeEditWiring, type VqeBufferAdapter, type VqeTabWiring } from '../edit/useVqeEditWiring';
import { createInlineEditorAdapter, createInlineModelAdapter } from './inlineModelAdapter';

/** SR-Edit document id prefix of the compact popover (`${INLINE_DOC_PREFIX}${tabId}`). */
export const INLINE_DOC_PREFIX = 'inline-toolbar:';

export type UseInlineToolbarEditContextArgs = {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly tabId: string;
  readonly query: string;
  readonly statementId: string | null;
  readonly options: ParseOptions;
  readonly connectionId?: string | null;
  readonly database?: string | null;
  /** SR-Edit document id prefix — Visual mode passes its own. */
  readonly documentPrefix?: string;
};

/** Monaco-backed wiring: `{ editContext, service }` (Visual mode needs the service for a blank buffer). */
export function useMonacoEditWiring(args: UseInlineToolbarEditContextArgs): VqeTabWiring {
  const { editorRef, documentPrefix = INLINE_DOC_PREFIX } = args;
  const adapter = useMemo<VqeBufferAdapter>(
    () => ({
      documentPrefix,
      stubConnectionPrefix: 'inline-tab-',
      createModel: (getSource) => createInlineModelAdapter(editorRef, getSource),
      editor: createInlineEditorAdapter(editorRef),
      // Authoritative live buffer = the Monaco value (undefined before the
      // handle mounts → the debounced `query` prop).
      getLiveSource: () => editorRef.current?.getValue(),
    }),
    [editorRef, documentPrefix],
  );
  return useVqeEditWiring({ ...args, adapter });
}

export function useInlineToolbarEditContext(
  args: UseInlineToolbarEditContextArgs,
): VqeEditContext | null {
  return useMonacoEditWiring(args).editContext;
}
